// src/components/AddUserForm.js
import React, { useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useTenant } from '../TenantProvider';
import { UserPlus, AlertCircle, Check } from 'lucide-react';

const AddUserForm = ({ onUserCreated }) => {
  const { getToken } = useAuth();
  const { companies, activeCompany } = useTenant();

  const [formData, setFormData] = useState({
    clerk_id: '',
    company_numeric_id: activeCompany?.numeric_id || '',
    first_name: '',
    last_name: '',
    email: '',
    title: '',
    country: '',
    role: 'Viewer'
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Role mapping
  const roles = [
    { name: 'Admin', id: 1 },
    { name: 'Analyst', id: 2 },
    { name: 'Viewer', id: 3 }
  ];

  const handleChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    // Validation
    if (!formData.clerk_id) {
      setError('Clerk ID is required');
      setLoading(false);
      return;
    }
    if (!formData.email) {
      setError('Email is required');
      setLoading(false);
      return;
    }
    if (!formData.first_name || !formData.last_name) {
      setError('First name and last name are required');
      setLoading(false);
      return;
    }
    if (!formData.company_numeric_id) {
      setError('Company must be selected');
      setLoading(false);
      return;
    }

    try {
      const token = await getToken();
      
      // Get role_id from role name
      const selectedRole = roles.find(r => r.name === formData.role);
      const role_id = selectedRole?.id || 3;

      const response = await fetch('/api/users', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          role_id,
          company_numeric_id: parseInt(formData.company_numeric_id, 10)
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create user');
      }

      const data = await response.json();
      setSuccess(`✅ User "${data.user.email}" created successfully!`);
      
      // Reset form
      setFormData({
        clerk_id: '',
        company_numeric_id: activeCompany?.numeric_id || '',
        first_name: '',
        last_name: '',
        email: '',
        title: '',
        country: '',
        role: 'Viewer'
      });

      // Call callback if provided
      if (onUserCreated) {
        setTimeout(() => {
          onUserCreated(data.user);
        }, 1500); // Give user time to see success message
      }
    } catch (err) {
      console.error('Error creating user:', err);
      setError(err.message || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 overflow-hidden flex flex-col max-h-[calc(100vh-240px)]">
        {/* Fixed Header */}
        <div className="px-6 py-4 border-b border-slate-700/50 bg-slate-900/50">
          <div className="flex items-center gap-3">
            <UserPlus className="w-6 h-6 text-purple-400" />
            <h3 className="text-xl font-bold text-slate-100">Add New User</h3>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Info Box */}
          <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
            <p className="text-sm text-blue-300">
              <strong>Note:</strong> The user must already exist in Clerk. You'll need their Clerk User ID.
            </p>
            <p className="text-xs text-blue-400 mt-1">
              Clerk Dashboard → Users → Select User → Copy User ID
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-red-400">Error</p>
                <p className="text-sm text-red-300 mt-1">{error}</p>
              </div>
            </div>
          )}

          {success && (
            <div className="mb-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg flex items-start gap-2">
              <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-green-400">Success</p>
                <p className="text-sm text-green-300 mt-1">{success}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            
            {/* Clerk ID */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">
                Clerk User ID <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={formData.clerk_id}
                onChange={handleChange('clerk_id')}
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/20 font-mono text-sm"
                placeholder="user_2xxxxxxxxxxxxx"
                required
                disabled={loading}
              />
              <p className="text-xs text-slate-500 mt-1">
                The unique Clerk ID (starts with "user_")
              </p>
            </div>

            {/* Company Selection */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">
                Company <span className="text-red-400">*</span>
              </label>
              <select
                value={formData.company_numeric_id}
                onChange={handleChange('company_numeric_id')}
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/20"
                required
                disabled={loading}
              >
                <option value="">Select a company</option>
                {companies?.map((company) => (
                  <option key={company.numeric_id} value={company.numeric_id}>
                    {company.name} (ID: {company.numeric_id})
                  </option>
                ))}
              </select>
            </div>

            {/* Name Fields */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">
                  First Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={handleChange('first_name')}
                  className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/20"
                  placeholder="John"
                  required
                  disabled={loading}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">
                  Last Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={handleChange('last_name')}
                  className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/20"
                  placeholder="Doe"
                  required
                  disabled={loading}
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">
                Email <span className="text-red-400">*</span>
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={handleChange('email')}
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/20"
                placeholder="john.doe@company.com"
                required
                disabled={loading}
              />
            </div>

            {/* Role */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">
                Role <span className="text-red-400">*</span>
              </label>
              <select
                value={formData.role}
                onChange={handleChange('role')}
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/20"
                required
                disabled={loading}
              >
                {roles.map((role) => (
                  <option key={role.id} value={role.name}>
                    {role.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-slate-500 mt-1">
                Admin: Full access • Analyst: Create/Update • Viewer: Read-only
              </p>
            </div>

            {/* Optional Fields */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">
                  Title (Optional)
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={handleChange('title')}
                  className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/20"
                  placeholder="Security Analyst"
                  disabled={loading}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">
                  Country (Optional)
                </label>
                <input
                  type="text"
                  value={formData.country}
                  onChange={handleChange('country')}
                  className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/20"
                  placeholder="United States"
                  disabled={loading}
                />
              </div>
            </div>
          </form>
        </div>

        {/* Fixed Footer with Submit Button */}
        <div className="px-6 py-4 border-t border-slate-700/50 bg-slate-900/50">
          <div className="flex justify-end">
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="px-6 py-2.5 bg-purple-500 hover:bg-purple-600 disabled:bg-slate-700 disabled:text-slate-500 disabled:cursor-not-allowed rounded-lg text-sm font-medium text-white transition-all flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Creating User...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4" />
                  Create User
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddUserForm;
